import { isDefined } from '@library/guards'
import type { Nullable } from '@library/types'
import { createMap, mapRecord } from '@library/utils'

import { getAllUserSettingsApi, setUserSettingsApi } from '../api'

class BackendStorage implements Storage {
  #storage: Partial<Record<string, Nullable<string>>> = {}
  constructor() {
    this.#fetchStorage()
  }

  #fetchStorage(key?: string, newValue?: Nullable<string>) {
    const oldValue = key ? this.#storage[key] : undefined

    getAllUserSettingsApi().then(res => {
      this.#storage = mapRecord(
        createMap(res, item => item.name),
        item => item?.value,
      )

      window.dispatchEvent(new StorageEvent('storage', { key, oldValue, newValue }))
      window.dispatchEvent(new StorageEvent('external-storage', { key, oldValue, newValue }))
    })
  }

  get length() {
    return Object.values(this.#storage).filter(isDefined).length
  }

  [index: string]: unknown

  async clear(): Promise<void> {
    await Promise.all(
      Object.entries(this.#storage)
        .filter(([, value]) => isDefined(value))
        .map(([key]) => setUserSettingsApi(key, null)),
    ).then(() => {
      this.#fetchStorage()
    })
  }

  getItem(key: string): string | null {
    return this.#storage[key] ?? null
  }

  key(index: number): string | null {
    return Object.keys(this.#storage).at(index) ?? null
  }

  removeItem(key: string): void {
    setUserSettingsApi(key, null).then(() => {
      this.#fetchStorage(key)
    })
  }

  setItem(key: string, value: string): void {
    setUserSettingsApi(key, value).then(() => {
      this.#fetchStorage(key, value)
    })
  }
}

let backendStorage: Storage | undefined

export function getBackendStorage() {
  backendStorage ??= new BackendStorage()
  return backendStorage
}
